import type {
  AppDatabase,
} from './store';
import type { Game, GameResult, Id, ScoreEvent, StandingsRecord, Team, Tournament } from '../models';
import { computeGoalieStats, computePlayerStats, isShootoutMade, resolvePeriodLengthMinutes, tallyGameEvents } from './scoringEngine';

export interface PublicTeamSummary {
  id: Id;
  name: string;
  shortCode: string;
  primaryColor: string;
}

export interface PublicScoreEvent {
  id: Id;
  type: ScoreEvent['type'];
  teamId?: Id;
  teamName?: string;
  playerName?: string;
  assistName?: string;
  goalieName?: string;
  period?: number;
  gameSecond?: number;
  recordedAt: string;
  label: string;
  penaltyMinutes?: number;
  shootoutRound?: number;
  shootoutAttemptNumber?: number;
  shootoutMade?: boolean;
  shootoutSuddenDeath?: boolean;
  contextLabels?: string[];
}

export interface PublicShootoutSummary {
  homeGoals: number;
  awayGoals: number;
  attempts: PublicScoreEvent[];
}

export interface PublicGameSnapshot {
  generatedAt: string;
  tournament: Pick<Tournament, 'id' | 'name' | 'slug' | 'format' | 'status'>;
  game: Game;
  divisionName?: string;
  startsAt?: string;
  rinkName?: string;
  periodLengthMinutes: number;
  homeTeam: PublicTeamSummary;
  awayTeam: PublicTeamSummary;
  score: {
    home: number;
    away: number;
    homeShots: number;
    awayShots: number;
    homePims: number;
    awayPims: number;
  };
  shootout: PublicShootoutSummary;
  result?: GameResult;
  timeline: PublicScoreEvent[];
  analytics: PublicGameAnalytics;
}

export interface PublicGameMomentumPoint {
  id: Id;
  label: string;
  type: 'start' | ScoreEvent['type'];
  teamId?: Id;
  period?: number;
  gameSecond?: number;
  recordedAt?: string;
  homeMomentum: number;
  awayMomentum: number;
  scoreHome: number;
  scoreAway: number;
  swing: number;
}

export interface PublicPeriodAnalytics {
  period: number;
  label: string;
  homeGoals: number;
  awayGoals: number;
  homeShots: number;
  awayShots: number;
  homePims: number;
  awayPims: number;
}

export interface PublicPlayerImpact {
  playerId: Id;
  playerName: string;
  teamId?: Id;
  teamName?: string;
  goals: number;
  assists: number;
  points: number;
  shots: number;
  penaltyMinutes: number;
  shootoutAttempts: number;
  shootoutGoals: number;
  impactScore: number;
  labels: string[];
}

export interface PublicGameInsight {
  label: string;
  value: string;
  detail: string;
  tone: 'neutral' | 'home' | 'away' | 'warn' | 'ok';
}

export interface PublicGameAnalytics {
  homeMomentum: number;
  awayMomentum: number;
  edgeTeamId?: Id;
  edgeLabel: string;
  leadChanges: number;
  totalGoals: number;
  biggestRun?: {
    teamId: Id;
    teamName: string;
    length: number;
  };
  periodBreakdown: PublicPeriodAnalytics[];
  playerImpact: PublicPlayerImpact[];
  keyInsights: PublicGameInsight[];
  specialTeams: {
    homePowerPlayGoals: number;
    awayPowerPlayGoals: number;
    homeShortHandedGoals: number;
    awayShortHandedGoals: number;
  };
  momentumTimeline: PublicGameMomentumPoint[];
}

export interface PublicEventMetric {
  label: string;
  value: string;
  detail: string;
}

export interface PublicEventTeamPulse {
  teamId: Id;
  teamName: string;
  shortCode: string;
  primaryColor: string;
  gamesPlayed: number;
  points: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifferential: number;
  penaltyMinutes: number;
  form: Array<'W' | 'L' | 'T'>;
  trendLabel: string;
}

export interface PublicEventInsight {
  label: string;
  value: string;
  detail: string;
  tone: 'neutral' | 'ok' | 'warn' | 'accent';
}

export interface PublicPlayerLeader {
  playerId: Id;
  playerName: string;
  teamId?: Id;
  teamName?: string;
  gamesPlayed: number;
  goals: number;
  assists: number;
  points: number;
  shots: number;
  penaltyMinutes: number;
  gameWinningGoals: number;
  powerPlayGoals: number;
  shortHandedGoals: number;
  shootoutAttempts: number;
  shootoutGoals: number;
  shootoutPct: number;
}

export interface PublicGoalieLeader {
  playerId: Id;
  playerName: string;
  teamId?: Id;
  teamName?: string;
  gamesPlayed: number;
  wins: number;
  losses: number;
  saves: number;
  goalsAgainst: number;
  shotsAgainst: number;
  savePct: number;
  gaa: number;
  shutouts: number;
  shootoutShotsAgainst: number;
  shootoutSaves: number;
  shootoutGoalsAgainst: number;
  shootoutSavePct: number;
}

export interface PublicEventLeaderboards {
  all: PublicPlayerLeader[];
  points: PublicPlayerLeader[];
  goals: PublicPlayerLeader[];
  assists: PublicPlayerLeader[];
  shots: PublicPlayerLeader[];
  penaltyMinutes: PublicPlayerLeader[];
}

export interface PublicEventAnalytics {
  metrics: PublicEventMetric[];
  teamPulse: PublicEventTeamPulse[];
  keyInsights: PublicEventInsight[];
  totals: {
    totalGames: number;
    liveGames: number;
    finalGames: number;
    totalGoals: number;
    totalPenaltyMinutes: number;
    oneGoalGames: number;
    averageGoalsPerFinal: number;
    averageMargin: number;
  };
}

export interface PublicEventScheduleRow {
  gameId: Id;
  divisionId?: Id;
  divisionName?: string;
  status: Game['status'];
  startsAt?: string;
  rinkName?: string;
  homeTeam: PublicTeamSummary;
  awayTeam: PublicTeamSummary;
  score: {
    home: number;
    away: number;
  };
  result?: Pick<GameResult, 'homeScore' | 'awayScore' | 'winningTeamId' | 'decidedBy' | 'endedAt'>;
}

export interface PublicEventSnapshot {
  generatedAt: string;
  tournament: Pick<Tournament, 'id' | 'name' | 'slug' | 'format' | 'status' | 'startsAt' | 'endsAt'>;
  liveGames: PublicGameSnapshot[];
  upcomingGames: PublicGameSnapshot[];
  recentFinals: PublicGameSnapshot[];
  schedule: PublicEventScheduleRow[];
  standings: StandingsRecord[];
  leaders: Array<{ playerId: Id; playerName: string; teamName?: string; goals: number; assists: number; points: number }>;
  playerLeaders: PublicEventLeaderboards;
  goalieLeaders: PublicGoalieLeader[];
  analytics: PublicEventAnalytics;
}

export function buildPublicGameSnapshot(db: AppDatabase, gameId: Id): PublicGameSnapshot | undefined {
  const game = db.games.find((row) => row.id === gameId);
  if (!game) return undefined;
  const tournament = db.tournaments.find((row) => row.id === game.tournamentId);
  const home = db.teams.find((row) => row.id === game.homeTeamId);
  const away = db.teams.find((row) => row.id === game.awayTeamId);
  if (!tournament || !home || !away) return undefined;
  const events = db.scoreEvents.filter((event) => event.gameId === game.id);
  const tally = tallyGameEvents(events, game);
  const result = db.gameResults.find((row) => row.gameId === game.id);
  const useOfficialResult = Boolean(result && (game.status === 'final' || game.status === 'forfeit'));
  const slot = game.scheduleSlotId ? db.scheduleSlots.find((row) => row.id === game.scheduleSlotId) : undefined;
  const rink = slot ? db.rinks.find((row) => row.id === slot.rinkId) : undefined;
  const division = db.divisions.find((row) => row.id === game.divisionId);
  const teamById = new Map(db.teams.map((team) => [team.id, team]));
  const eventById = new Map(events.map((event) => [event.id, event]));
  const periodLengthMinutes = resolvePeriodLengthMinutes(tournament.settings.scoring);
  const goalContextByEventId = buildGoalContextLabels(
    events,
    game,
    periodLengthMinutes,
    tournament.settings.scoring.periodCount ?? 3,
  );
  const shootoutAttempts = events
    .filter((event) => event.type === 'shootout_attempt')
    .sort(compareScoreEvents)
    .map((event) => toPublicEvent(db, teamById, eventById, event, goalContextByEventId.get(event.id)));
  return {
    generatedAt: new Date().toISOString(),
    tournament: {
      id: tournament.id,
      name: tournament.name,
      slug: tournament.slug,
      format: tournament.format,
      status: tournament.status,
    },
    game,
    divisionName: division?.name,
    startsAt: slot?.startsAt,
    rinkName: rink?.name,
    periodLengthMinutes,
    homeTeam: toPublicTeam(home),
    awayTeam: toPublicTeam(away),
    score: {
      home: useOfficialResult ? result!.homeScore : tally.homeScore,
      away: useOfficialResult ? result!.awayScore : tally.awayScore,
      homeShots: tally.homeShots,
      awayShots: tally.awayShots,
      homePims: tally.homePims,
      awayPims: tally.awayPims,
    },
    shootout: {
      homeGoals: tally.homeShootoutGoals,
      awayGoals: tally.awayShootoutGoals,
      attempts: shootoutAttempts,
    },
    result,
    timeline: events
      .filter((event) => event.type !== 'assist')
      .sort(compareScoreEvents)
      .map((event) => toPublicEvent(db, teamById, eventById, event, goalContextByEventId.get(event.id))),
    analytics: buildPublicGameAnalytics(db, game, home, away, events, result, tournament.settings.scoring.periodCount ?? 3),
  };
}

export function buildPublicEventSnapshot(db: AppDatabase, tournamentIdOrSlug: Id): PublicEventSnapshot | undefined {
  const tournament = db.tournaments.find((row) => row.id === tournamentIdOrSlug || row.slug === tournamentIdOrSlug);
  if (!tournament) return undefined;
  const games = db.games.filter((game) => game.tournamentId === tournament.id);
  const snapshots = games
    .map((game) => buildPublicGameSnapshot(db, game.id))
    .filter(Boolean) as PublicGameSnapshot[];
  const byStart = (a: PublicGameSnapshot, b: PublicGameSnapshot) => (a.startsAt ?? '').localeCompare(b.startsAt ?? '');
  const leaders = buildLeaders(db, tournament.id);
  const playerLeaders = buildPlayerLeaderboards(db, tournament.id);
  const goalieLeaders = buildGoalieLeaderboards(db, tournament.id);
  const standings = db.standings
    .filter((row) => row.tournamentId === tournament.id)
    .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  return {
    generatedAt: new Date().toISOString(),
    tournament: {
      id: tournament.id,
      name: tournament.name,
      slug: tournament.slug,
      format: tournament.format,
      status: tournament.status,
      startsAt: tournament.startsAt,
      endsAt: tournament.endsAt,
    },
    liveGames: snapshots.filter((row) => row.game.status === 'in_progress').sort(byStart),
    upcomingGames: snapshots
      .filter((row) => row.game.status === 'scheduled' || row.game.status === 'ready')
      .sort(byStart)
      .slice(0, 8),
    recentFinals: snapshots
      .filter((row) => row.game.status === 'final' || row.game.status === 'forfeit')
      .sort((a, b) => (b.result?.endedAt ?? b.startsAt ?? '').localeCompare(a.result?.endedAt ?? a.startsAt ?? ''))
      .slice(0, 6),
    schedule: snapshots.sort(byStart).map(toPublicScheduleRow),
    standings,
    leaders,
    playerLeaders,
    goalieLeaders,
    analytics: buildPublicEventAnalytics(db, tournament, snapshots, standings),
  };
}

function toPublicScheduleRow(snapshot: PublicGameSnapshot): PublicEventScheduleRow {
  return {
    gameId: snapshot.game.id,
    divisionId: snapshot.game.divisionId,
    divisionName: snapshot.divisionName,
    status: snapshot.game.status,
    startsAt: snapshot.startsAt,
    rinkName: snapshot.rinkName,
    homeTeam: snapshot.homeTeam,
    awayTeam: snapshot.awayTeam,
    score: {
      home: snapshot.score.home,
      away: snapshot.score.away,
    },
    result: snapshot.result ? {
      homeScore: snapshot.result.homeScore,
      awayScore: snapshot.result.awayScore,
      winningTeamId: snapshot.result.winningTeamId,
      decidedBy: snapshot.result.decidedBy,
      endedAt: snapshot.result.endedAt,
    } : undefined,
  };
}

function toPublicTeam(team: Team): PublicTeamSummary {
  return {
    id: team.id,
    name: team.name,
    shortCode: team.shortCode,
    primaryColor: team.primaryColor,
  };
}

function toPublicEvent(
  db: AppDatabase,
  teamById: Map<Id, Team>,
  eventById: Map<Id, ScoreEvent>,
  event: ScoreEvent,
  contextLabels: string[] = [],
): PublicScoreEvent {
  const player = event.playerId ? db.players.find((row) => row.id === event.playerId) : undefined;
  const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
  const assist = db.scoreEvents.find((row) => row.parentEventId === event.id && row.type === 'assist');
  const assistPlayer = assist?.playerId ? db.players.find((row) => row.id === assist.playerId) : undefined;
  const assistProfile = assistPlayer ? db.profiles.find((row) => row.id === assistPlayer.profileId) : undefined;
  const goalie = event.goalieId ? db.players.find((row) => row.id === event.goalieId) : undefined;
  const goalieProfile = goalie ? db.profiles.find((row) => row.id === goalie.profileId) : undefined;
  const parent = event.parentEventId ? eventById.get(event.parentEventId) : undefined;
  return {
    id: event.id,
    type: event.type,
    teamId: event.teamId,
    teamName: event.teamId ? teamById.get(event.teamId)?.name : undefined,
    playerName: profile?.displayName,
    assistName: assistProfile?.displayName,
    goalieName: goalieProfile?.displayName,
    period: event.period,
    gameSecond: event.gameSecond,
    recordedAt: event.recordedAt,
    penaltyMinutes: event.penaltyMinutes,
    shootoutRound: event.shootoutRound,
    shootoutAttemptNumber: event.shootoutAttemptNumber,
    shootoutMade: event.shootoutMade,
    shootoutSuddenDeath: event.shootoutSuddenDeath,
    contextLabels,
    label: eventLabel(event, profile?.displayName, assistProfile?.displayName, parent, goalieProfile?.displayName),
  };
}

function buildGoalContextLabels(
  events: ScoreEvent[],
  game: Game,
  periodLengthMinutes: number,
  regulationPeriodCount: number,
): Map<Id, string[]> {
  const labels = new Map<Id, string[]>();
  const goals = events
    .filter((event) => event.type === 'goal' || event.type === 'penalty_shot_made')
    .sort(compareScoreEvents);
  let homeScore = 0;
  let awayScore = 0;
  const regulationSeconds = Math.max(1, regulationPeriodCount) * Math.max(60, periodLengthMinutes * 60);
  for (const event of goals) {
    const beforeHome = homeScore;
    const beforeAway = awayScore;
    if (event.teamId === game.homeTeamId) homeScore += 1;
    if (event.teamId === game.awayTeamId) awayScore += 1;
    const teamBefore = event.teamId === game.homeTeamId ? beforeHome : beforeAway;
    const oppBefore = event.teamId === game.homeTeamId ? beforeAway : beforeHome;
    const teamAfter = event.teamId === game.homeTeamId ? homeScore : awayScore;
    const oppAfter = event.teamId === game.homeTeamId ? awayScore : homeScore;
    const row: string[] = [];
    if (beforeHome === 0 && beforeAway === 0) row.push('First goal');
    if (teamAfter === oppAfter && teamBefore < oppBefore) row.push('Tying goal');
    if (teamAfter > oppAfter && teamBefore <= oppBefore) row.push('Go-ahead goal');
    if (teamBefore > oppBefore && teamAfter > oppAfter) row.push('Insurance');
    if (teamBefore < oppBefore && teamAfter < oppAfter) row.push('Comeback pressure');
    const gameSecond = event.gameSecond;
    if (gameSecond !== undefined) {
      if (gameSecond <= 120) row.push('Early');
      const isLateRegulation = gameSecond >= regulationSeconds - 120;
      if (isLateRegulation) row.push('Late');
      if (isLateRegulation && row.some((label) => label === 'Tying goal' || label === 'Go-ahead goal')) {
        row.push('Clutch');
      }
    }
    if (event.isPowerPlay) row.push('Power play');
    if (event.isShortHanded) row.push('Short-handed');
    if (row.length > 0) labels.set(event.id, row);
  }
  return labels;
}

function eventLabel(event: ScoreEvent, playerName?: string, assistName?: string, parent?: ScoreEvent, goalieName?: string): string {
  if (event.type === 'goal') {
    const scorer = playerName ?? 'Goal';
    const suffix = assistName ? ` from ${assistName}` : '';
    if (event.isPowerPlay) return `${scorer} power-play goal${suffix}`;
    if (event.isShortHanded) return `${scorer} short-handed goal${suffix}`;
    if (event.isEmptyNet) return `${scorer} empty-net goal${suffix}`;
    return `${scorer} goal${suffix}`;
  }
  if (event.type === 'penalty') return `${playerName ?? 'Team'} penalty${event.penaltyType ? `: ${event.penaltyType}` : ''}`;
  if (event.type === 'shot_on_goal') return `${playerName ?? 'Team'} shot on goal`;
  if (event.type === 'penalty_shot_made') return `${playerName ?? 'Player'} scored on a penalty shot`;
  if (event.type === 'penalty_shot_missed') return `${playerName ?? 'Player'} missed a penalty shot`;
  if (event.type === 'shootout_attempt') {
    const result = isShootoutMade(event) ? 'scored' : 'missed';
    const goalie = goalieName ? ` vs ${goalieName}` : '';
    const round = event.shootoutRound ? `Round ${event.shootoutRound}: ` : '';
    return `${round}${playerName ?? 'Player'} ${result} in the shootout${goalie}`;
  }
  if (event.type === 'assist' && parent) return `${playerName ?? 'Assist'} on goal`;
  if (event.type === 'note') return event.notes ?? 'Scorekeeper note';
  return event.type.replaceAll('_', ' ');
}

function buildPublicGameAnalytics(
  db: AppDatabase,
  game: Game,
  home: Team,
  away: Team,
  events: ScoreEvent[],
  result: GameResult | undefined,
  periodCount: number,
): PublicGameAnalytics {
  const orderedEvents = [...events].sort(compareScoreEvents);
  const homeTeamId = game.homeTeamId;
  const awayTeamId = game.awayTeamId;
  const periodBreakdown = buildPeriodBreakdown(orderedEvents, game, periodCount);
  const playerImpact = buildPlayerImpact(db, orderedEvents);
  const specialTeams = {
    homePowerPlayGoals: orderedEvents.filter((event) => event.teamId === homeTeamId && event.type === 'goal' && event.isPowerPlay).length,
    awayPowerPlayGoals: orderedEvents.filter((event) => event.teamId === awayTeamId && event.type === 'goal' && event.isPowerPlay).length,
    homeShortHandedGoals: orderedEvents.filter((event) => event.teamId === homeTeamId && event.type === 'goal' && event.isShortHanded).length,
    awayShortHandedGoals: orderedEvents.filter((event) => event.teamId === awayTeamId && event.type === 'goal' && event.isShortHanded).length,
  };
  const momentumTimeline = buildMomentumTimeline(db, game, home, away, orderedEvents);
  const latestMomentum = momentumTimeline[momentumTimeline.length - 1];
  const homeMomentum = latestMomentum?.homeMomentum ?? 50;
  const awayMomentum = latestMomentum?.awayMomentum ?? 50;
  const edgeTeamId = Math.abs(homeMomentum - awayMomentum) >= 8
    ? homeMomentum > awayMomentum
      ? homeTeamId
      : awayTeamId
    : undefined;
  const edgeLabel = edgeTeamId
    ? `${edgeTeamId === homeTeamId ? home.shortCode : away.shortCode} momentum edge`
    : 'Even momentum';
  const flow = analyzeScoringFlow(orderedEvents, game, home, away, result);
  const keyInsights = buildGameInsights({
    home,
    away,
    result,
    scoreFlow: flow,
    periodBreakdown,
    playerImpact,
    specialTeams,
    homeMomentum,
    awayMomentum,
    edgeTeamId,
  });
  return {
    homeMomentum,
    awayMomentum,
    edgeTeamId,
    edgeLabel,
    leadChanges: flow.leadChanges,
    totalGoals: flow.totalGoals,
    biggestRun: flow.biggestRun,
    periodBreakdown,
    playerImpact,
    keyInsights,
    specialTeams,
    momentumTimeline,
  };
}

function buildMomentumTimeline(
  db: AppDatabase,
  game: Game,
  home: Team,
  away: Team,
  events: ScoreEvent[],
): PublicGameMomentumPoint[] {
  const timeline: PublicGameMomentumPoint[] = [{
    id: `${game.id}-start`,
    label: 'Puck drop',
    type: 'start',
    homeMomentum: 50,
    awayMomentum: 50,
    scoreHome: 0,
    scoreAway: 0,
    swing: 0,
  }];
  let homeMomentum = 50;
  let scoreHome = 0;
  let scoreAway = 0;
  for (const event of events) {
    const isGoal = event.type === 'goal' || event.type === 'penalty_shot_made';
    const isShot = event.type === 'shot_on_goal';
    const isPenalty = event.type === 'penalty';
    if (!isGoal && !isShot && !isPenalty) continue;

    const before = homeMomentum;
    let delta = 0;
    if (isGoal) {
      const homeBefore = scoreHome;
      const awayBefore = scoreAway;
      if (event.teamId === game.homeTeamId) scoreHome += 1;
      if (event.teamId === game.awayTeamId) scoreAway += 1;
      const tiedBefore = homeBefore === awayBefore;
      const tiedAfter = scoreHome === scoreAway;
      const leadChanged =
        !tiedBefore &&
        !tiedAfter &&
        (homeBefore > awayBefore) !== (scoreHome > scoreAway);
      delta = 18;
      if (event.isPowerPlay) delta += 3;
      if (event.isShortHanded) delta += 5;
      if (tiedAfter) delta += 4;
      if (leadChanged) delta += 7;
      if (event.teamId === game.awayTeamId) delta *= -1;
    } else if (isShot) {
      delta = event.teamId === game.homeTeamId ? 4 : event.teamId === game.awayTeamId ? -4 : 0;
    } else if (isPenalty) {
      delta = event.teamId === game.homeTeamId ? -6 : event.teamId === game.awayTeamId ? 6 : 0;
    }

    homeMomentum = clamp(Math.round(homeMomentum + delta), 5, 95);
    const player = resolveEventPlayerName(db, event);
    const label = isGoal
      ? `${event.teamId === game.homeTeamId ? home.shortCode : away.shortCode} goal${player ? ` by ${player}` : ''}`
      : isShot
        ? `${event.teamId === game.homeTeamId ? home.shortCode : away.shortCode} shot`
        : `${event.teamId === game.homeTeamId ? home.shortCode : away.shortCode} penalty`;
    timeline.push({
      id: event.id,
      label,
      type: event.type,
      teamId: event.teamId,
      period: event.period,
      gameSecond: event.gameSecond,
      recordedAt: event.recordedAt,
      homeMomentum,
      awayMomentum: 100 - homeMomentum,
      scoreHome,
      scoreAway,
      swing: Math.abs(homeMomentum - before),
    });
  }
  return timeline;
}

function buildPeriodBreakdown(events: ScoreEvent[], game: Game, periodCount: number): PublicPeriodAnalytics[] {
  const periods = new Map<number, PublicPeriodAnalytics>();
  const ensurePeriod = (period: number): PublicPeriodAnalytics => {
    const safePeriod = Math.max(1, Number.isFinite(period) ? Math.floor(period) : 1);
    const existing = periods.get(safePeriod);
    if (existing) return existing;
    const row: PublicPeriodAnalytics = {
      period: safePeriod,
      label: periodLabel(safePeriod, periodCount),
      homeGoals: 0,
      awayGoals: 0,
      homeShots: 0,
      awayShots: 0,
      homePims: 0,
      awayPims: 0,
    };
    periods.set(safePeriod, row);
    return row;
  };
  for (let period = 1; period <= Math.max(1, periodCount); period += 1) ensurePeriod(period);
  for (const event of events) {
    const row = ensurePeriod(event.period ?? 1);
    const isHome = event.teamId === game.homeTeamId;
    const isAway = event.teamId === game.awayTeamId;
    if (!isHome && !isAway) continue;
    if (event.type === 'goal' || event.type === 'penalty_shot_made') {
      if (isHome) row.homeGoals += 1;
      if (isAway) row.awayGoals += 1;
    }
    if (event.type === 'shot_on_goal') {
      if (isHome) row.homeShots += 1;
      if (isAway) row.awayShots += 1;
    }
    if (event.type === 'penalty') {
      if (isHome) row.homePims += event.penaltyMinutes ?? 2;
      if (isAway) row.awayPims += event.penaltyMinutes ?? 2;
    }
  }
  return Array.from(periods.values()).sort((a, b) => a.period - b.period);
}

function buildPlayerImpact(db: AppDatabase, events: ScoreEvent[]): PublicPlayerImpact[] {
  const rows = new Map<Id, PublicPlayerImpact>();
  const ensure = (playerId: Id, teamId?: Id): PublicPlayerImpact => {
    const existing = rows.get(playerId);
    if (existing) return existing;
    const player = db.players.find((row) => row.id === playerId);
    const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
    const team = teamId ? db.teams.find((row) => row.id === teamId) : undefined;
    const row: PublicPlayerImpact = {
      playerId,
      playerName: profile?.displayName ?? 'Unknown player',
      teamId,
      teamName: team?.name,
      goals: 0,
      assists: 0,
      points: 0,
      shots: 0,
      penaltyMinutes: 0,
      shootoutAttempts: 0,
      shootoutGoals: 0,
      impactScore: 0,
      labels: [],
    };
    rows.set(playerId, row);
    return row;
  };
  for (const event of events) {
    if (!event.playerId) continue;
    const row = ensure(event.playerId, event.teamId);
    if (event.type === 'goal' || event.type === 'penalty_shot_made') row.goals += 1;
    if (event.type === 'assist') row.assists += 1;
    if (event.type === 'shot_on_goal') row.shots += 1;
    if (event.type === 'penalty') row.penaltyMinutes += event.penaltyMinutes ?? 2;
    if (event.type === 'shootout_attempt') {
      row.shootoutAttempts += 1;
      if (isShootoutMade(event)) row.shootoutGoals += 1;
    }
    row.points = row.goals + row.assists;
    row.impactScore =
      row.goals * 6 +
      row.assists * 4 +
      row.shootoutGoals * 2 +
      row.shots * 1.2 -
      row.penaltyMinutes * 0.75;
  }
  for (const row of rows.values()) {
    const labels: string[] = [];
    if (row.goals >= 3) labels.push('Hat trick');
    else if (row.goals === 2) labels.push('Hat trick watch');
    if (row.points >= 3) labels.push(`${row.points} points`);
    if (row.shootoutGoals > 0) labels.push(`${row.shootoutGoals}/${row.shootoutAttempts} SO`);
    if (row.shots >= 4) labels.push('Shot pressure');
    if (row.penaltyMinutes >= 4) labels.push(`${row.penaltyMinutes} PIM`);
    row.labels = labels;
  }
  return Array.from(rows.values())
    .sort((a, b) => b.impactScore - a.impactScore || b.points - a.points || b.goals - a.goals)
    .slice(0, 5);
}

function analyzeScoringFlow(
  events: ScoreEvent[],
  game: Game,
  home: Team,
  away: Team,
  result: GameResult | undefined,
): {
  leadChanges: number;
  totalGoals: number;
  biggestRun?: { teamId: Id; teamName: string; length: number };
  winnerTrailed: boolean;
} {
  let homeScore = 0;
  let awayScore = 0;
  let previousLeader: Id | undefined;
  let leadChanges = 0;
  let currentRunTeamId: Id | undefined;
  let currentRunLength = 0;
  let biggestRunTeamId: Id | undefined;
  let biggestRunLength = 0;
  let winnerTrailed = false;
  const winnerTeamId = result?.winningTeamId;
  const goalEvents = events.filter((event) => event.type === 'goal' || event.type === 'penalty_shot_made');
  for (const event of goalEvents) {
    if (event.teamId === game.homeTeamId) homeScore += 1;
    if (event.teamId === game.awayTeamId) awayScore += 1;
    const leader = homeScore === awayScore ? undefined : homeScore > awayScore ? game.homeTeamId : game.awayTeamId;
    if (leader && previousLeader && leader !== previousLeader) leadChanges += 1;
    if (leader !== previousLeader) previousLeader = leader;
    if (winnerTeamId) {
      const winnerScore = winnerTeamId === game.homeTeamId ? homeScore : awayScore;
      const opponentScore = winnerTeamId === game.homeTeamId ? awayScore : homeScore;
      if (opponentScore > winnerScore) winnerTrailed = true;
    }
    if (event.teamId && event.teamId === currentRunTeamId) {
      currentRunLength += 1;
    } else {
      currentRunTeamId = event.teamId;
      currentRunLength = 1;
    }
    if (currentRunTeamId && currentRunLength > biggestRunLength) {
      biggestRunTeamId = currentRunTeamId;
      biggestRunLength = currentRunLength;
    }
  }
  const biggestRunTeam = biggestRunTeamId ? getTeamNameForGame(game, home, away, biggestRunTeamId) : undefined;
  return {
    leadChanges,
    totalGoals: goalEvents.length,
    biggestRun: biggestRunTeamId && biggestRunTeam && biggestRunLength >= 2
      ? { teamId: biggestRunTeamId, teamName: biggestRunTeam, length: biggestRunLength }
      : undefined,
    winnerTrailed,
  };
}

function buildGameInsights(input: {
  home: Team;
  away: Team;
  result?: GameResult;
  scoreFlow: ReturnType<typeof analyzeScoringFlow>;
  periodBreakdown: PublicPeriodAnalytics[];
  playerImpact: PublicPlayerImpact[];
  specialTeams: PublicGameAnalytics['specialTeams'];
  homeMomentum: number;
  awayMomentum: number;
  edgeTeamId?: Id;
}): PublicGameInsight[] {
  const insights: PublicGameInsight[] = [];
  if (input.edgeTeamId) {
    const team = input.edgeTeamId === input.home.id ? input.home : input.away;
    const value = input.edgeTeamId === input.home.id ? `${input.homeMomentum}%` : `${input.awayMomentum}%`;
    insights.push({
      label: 'Momentum',
      value,
      detail: `${team.name} has the current game-flow edge.`,
      tone: input.edgeTeamId === input.home.id ? 'home' : 'away',
    });
  } else {
    insights.push({
      label: 'Momentum',
      value: 'Even',
      detail: 'The game flow is balanced based on recorded goals, shots, and penalties.',
      tone: 'neutral',
    });
  }
  if (input.scoreFlow.leadChanges > 0) {
    insights.push({
      label: 'Lead changes',
      value: String(input.scoreFlow.leadChanges),
      detail: 'This game has changed direction on the scoreboard.',
      tone: 'ok',
    });
  }
  if (input.scoreFlow.biggestRun) {
    insights.push({
      label: 'Scoring run',
      value: `${input.scoreFlow.biggestRun.length}-0`,
      detail: `${input.scoreFlow.biggestRun.teamName} owns the largest scoring run.`,
      tone: input.scoreFlow.biggestRun.teamId === input.home.id ? 'home' : 'away',
    });
  }
  if (input.result?.winningTeamId && input.scoreFlow.winnerTrailed) {
    const winner = input.result.winningTeamId === input.home.id ? input.home : input.away;
    insights.push({
      label: 'Comeback',
      value: winner.shortCode,
      detail: `${winner.name} trailed before finishing the game.`,
      tone: input.result.winningTeamId === input.home.id ? 'home' : 'away',
    });
  }
  const topPlayer = input.playerImpact[0];
  if (topPlayer) {
    insights.push({
      label: 'Impact player',
      value: topPlayer.playerName,
      detail: `${topPlayer.points} point${topPlayer.points === 1 ? '' : 's'} in this game.`,
      tone: topPlayer.teamId === input.home.id ? 'home' : topPlayer.teamId === input.away.id ? 'away' : 'neutral',
    });
  }
  const homeSpecial = input.specialTeams.homePowerPlayGoals + input.specialTeams.homeShortHandedGoals;
  const awaySpecial = input.specialTeams.awayPowerPlayGoals + input.specialTeams.awayShortHandedGoals;
  if (homeSpecial > 0 || awaySpecial > 0) {
    const team = homeSpecial >= awaySpecial ? input.home : input.away;
    insights.push({
      label: 'Special teams',
      value: team.shortCode,
      detail: `${team.name} has the special-teams scoring edge.`,
      tone: team.id === input.home.id ? 'home' : 'away',
    });
  }
  return insights.slice(0, 4);
}

function compareScoreEvents(a: ScoreEvent, b: ScoreEvent): number {
  const aSecond = Number.isFinite(a.gameSecond) ? a.gameSecond! : Number.POSITIVE_INFINITY;
  const bSecond = Number.isFinite(b.gameSecond) ? b.gameSecond! : Number.POSITIVE_INFINITY;
  if (aSecond !== bSecond) return aSecond - bSecond;
  return a.recordedAt.localeCompare(b.recordedAt);
}

function periodLabel(period: number, regulationPeriodCount: number): string {
  if (period === regulationPeriodCount + 1) return 'OT';
  if (period > regulationPeriodCount + 1) return 'SO';
  return `P${period}`;
}

function resolveEventPlayerName(db: AppDatabase, event: ScoreEvent): string {
  const player = event.playerId ? db.players.find((row) => row.id === event.playerId) : undefined;
  const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
  return profile?.displayName ?? '';
}

function getTeamNameForGame(game: Game, home: Team, away: Team, teamId: Id): string {
  if (teamId === home.id) return home.name;
  if (teamId === away.id) return away.name;
  if (teamId === game.homeTeamId) return 'Home';
  if (teamId === game.awayTeamId) return 'Away';
  return 'Team';
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function buildPublicEventAnalytics(
  db: AppDatabase,
  tournament: Tournament,
  snapshots: PublicGameSnapshot[],
  standings: StandingsRecord[],
): PublicEventAnalytics {
  const liveGames = snapshots.filter((snapshot) => snapshot.game.status === 'in_progress');
  const upcomingGames = snapshots.filter((snapshot) => snapshot.game.status === 'scheduled' || snapshot.game.status === 'ready');
  const finalGames = snapshots.filter((snapshot) => snapshot.game.status === 'final' || snapshot.game.status === 'forfeit');
  const totalGoals = finalGames.reduce((sum, snapshot) => sum + snapshot.score.home + snapshot.score.away, 0);
  const totalPenaltyMinutes = finalGames.reduce((sum, snapshot) => sum + snapshot.score.homePims + snapshot.score.awayPims, 0);
  const oneGoalGames = finalGames.filter((snapshot) => Math.abs(snapshot.score.home - snapshot.score.away) === 1).length;
  const totalMargin = finalGames.reduce((sum, snapshot) => sum + Math.abs(snapshot.score.home - snapshot.score.away), 0);
  const averageGoalsPerFinal = finalGames.length ? totalGoals / finalGames.length : 0;
  const averageMargin = finalGames.length ? totalMargin / finalGames.length : 0;
  const leaders = buildLeaders(db, tournament.id);

  const metrics: PublicEventMetric[] = [
    {
      label: 'Live now',
      value: String(liveGames.length),
      detail: liveGames.length > 0
        ? `${liveGames.length} game${liveGames.length === 1 ? '' : 's'} updating from the scorer table.`
        : `${upcomingGames.length} upcoming game${upcomingGames.length === 1 ? '' : 's'} on deck.`,
    },
    {
      label: 'Final scores',
      value: String(finalGames.length),
      detail: finalGames.length > 0
        ? `${totalGoals} goal${totalGoals === 1 ? '' : 's'} recorded in completed games.`
        : 'Official results will appear as scorekeepers submit games.',
    },
    {
      label: 'Scoring pace',
      value: finalGames.length > 0 ? `${formatNumber(averageGoalsPerFinal)} G/game` : 'Pending',
      detail: finalGames.length > 0
        ? 'Average combined scoring across finalized games.'
        : 'Pace unlocks after the first final score.',
    },
    {
      label: 'Tight games',
      value: finalGames.length > 0 ? String(oneGoalGames) : 'Pending',
      detail: finalGames.length > 0
        ? `${formatNumber(averageMargin)} average goal margin.`
        : 'One-goal games and margin trends will show here.',
    },
  ];

  const teamPulse = buildTeamPulse(db, standings, finalGames);
  const keyInsights = buildEventInsights({
    tournament,
    liveGames,
    finalGames,
    standings,
    leaders,
    oneGoalGames,
    averageGoalsPerFinal,
    totalPenaltyMinutes,
    teamPulse,
  });

  return {
    metrics,
    teamPulse,
    keyInsights,
    totals: {
      totalGames: snapshots.length,
      liveGames: liveGames.length,
      finalGames: finalGames.length,
      totalGoals,
      totalPenaltyMinutes,
      oneGoalGames,
      averageGoalsPerFinal,
      averageMargin,
    },
  };
}

function buildTeamPulse(
  db: AppDatabase,
  standings: StandingsRecord[],
  finalGames: PublicGameSnapshot[],
): PublicEventTeamPulse[] {
  const teamById = new Map(db.teams.map((team) => [team.id, team]));
  return standings
    .slice(0, 8)
    .map((standing) => {
      const team = teamById.get(standing.teamId);
      const goalDifferential = standing.goalsFor - standing.goalsAgainst;
      return {
        teamId: standing.teamId,
        teamName: team?.name ?? 'Team',
        shortCode: team?.shortCode ?? 'TM',
        primaryColor: team?.primaryColor ?? '#3b82f6',
        gamesPlayed: standing.gamesPlayed,
        points: standing.points ?? 0,
        goalsFor: standing.goalsFor,
        goalsAgainst: standing.goalsAgainst,
        goalDifferential,
        penaltyMinutes: standing.penaltyMinutes ?? 0,
        form: buildTeamForm(standing.teamId, finalGames),
        trendLabel: teamTrendLabel(standing, goalDifferential),
      };
    });
}

function buildTeamForm(teamId: Id, finalGames: PublicGameSnapshot[]): Array<'W' | 'L' | 'T'> {
  return [...finalGames]
    .filter((snapshot) => snapshot.game.homeTeamId === teamId || snapshot.game.awayTeamId === teamId)
    .sort((a, b) => (b.result?.endedAt ?? b.startsAt ?? '').localeCompare(a.result?.endedAt ?? a.startsAt ?? ''))
    .slice(0, 5)
    .map((snapshot) => {
      const isHome = snapshot.game.homeTeamId === teamId;
      const teamScore = isHome ? snapshot.score.home : snapshot.score.away;
      const opponentScore = isHome ? snapshot.score.away : snapshot.score.home;
      if (teamScore === opponentScore) return 'T';
      return teamScore > opponentScore ? 'W' : 'L';
    });
}

function teamTrendLabel(standing: StandingsRecord, goalDifferential: number): string {
  if ((standing.rank ?? 99) === 1) return 'Setting the pace';
  if (goalDifferential >= 3) return 'Goal edge';
  if ((standing.penaltyMinutes ?? 0) <= 2 && standing.gamesPlayed > 0) return 'Clean play';
  if ((standing.points ?? 0) > 0) return 'In the mix';
  return 'Building rhythm';
}

function buildEventInsights(input: {
  tournament: Tournament;
  liveGames: PublicGameSnapshot[];
  finalGames: PublicGameSnapshot[];
  standings: StandingsRecord[];
  leaders: PublicEventSnapshot['leaders'];
  oneGoalGames: number;
  averageGoalsPerFinal: number;
  totalPenaltyMinutes: number;
  teamPulse: PublicEventTeamPulse[];
}): PublicEventInsight[] {
  const insights: PublicEventInsight[] = [];
  const topTeam = input.teamPulse[0];
  const topScorer = input.leaders[0];
  if (input.liveGames.length > 0) {
    insights.push({
      label: 'Live game center',
      value: String(input.liveGames.length),
      detail: `${input.liveGames.length} game${input.liveGames.length === 1 ? ' is' : 's are'} updating on the public site.`,
      tone: 'accent',
    });
  }
  if (topTeam && input.standings.length > 0) {
    insights.push({
      label: input.tournament.format === 'season' ? 'Season leader' : 'Event leader',
      value: topTeam.shortCode,
      detail: `${topTeam.teamName} leads with ${topTeam.points} point${topTeam.points === 1 ? '' : 's'} and a ${signedNumber(topTeam.goalDifferential)} goal differential.`,
      tone: 'ok',
    });
  }
  if (topScorer) {
    insights.push({
      label: 'Points leader',
      value: topScorer.playerName,
      detail: `${topScorer.points} point${topScorer.points === 1 ? '' : 's'} from ${topScorer.goals} goal${topScorer.goals === 1 ? '' : 's'} and ${topScorer.assists} assist${topScorer.assists === 1 ? '' : 's'}.`,
      tone: 'neutral',
    });
  }
  if (input.finalGames.length > 0) {
    insights.push({
      label: 'Game profile',
      value: `${formatNumber(input.averageGoalsPerFinal)} G/game`,
      detail: `${input.oneGoalGames} one-goal final${input.oneGoalGames === 1 ? '' : 's'} and ${input.totalPenaltyMinutes} total PIM in completed games.`,
      tone: input.oneGoalGames > 0 ? 'accent' : 'neutral',
    });
  }
  if (insights.length === 0) {
    insights.push({
      label: 'Data coming online',
      value: 'Soon',
      detail: 'Live games, standings, leaders, and season trends wake up as scorekeepers submit results.',
      tone: 'neutral',
    });
  }
  return insights.slice(0, 4);
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return value % 1 === 0 ? String(value) : value.toFixed(1);
}

function signedNumber(value: number): string {
  if (value > 0) return `+${value}`;
  return String(value);
}

function buildPlayerLeaderboards(db: AppDatabase, tournamentId: Id): PublicEventLeaderboards {
  const games = db.games.filter((game) => game.tournamentId === tournamentId);
  const gameIds = new Set(games.map((game) => game.id));
  const stats = computePlayerStats({
    tournamentId,
    games,
    events: db.scoreEvents.filter((event) => gameIds.has(event.gameId)),
    rosterEntries: db.rosterEntries
      .filter((entry) => entry.tournamentId === tournamentId)
      .map((entry) => ({ teamId: entry.teamId, playerId: entry.playerId })),
    gameRosters: db.gameRosters.filter((row) => gameIds.has(row.gameId)),
  }).map((row): PublicPlayerLeader => {
    const player = db.players.find((item) => item.id === row.playerId);
    const profile = player ? db.profiles.find((item) => item.id === player.profileId) : undefined;
    const team = db.teams.find((item) => item.id === row.teamId);
    return {
      playerId: row.playerId,
      playerName: profile?.displayName ?? 'Unknown player',
      teamId: row.teamId,
      teamName: team?.name,
      gamesPlayed: row.gamesPlayed,
      goals: row.goals,
      assists: row.assists,
      points: row.points,
      shots: row.shots,
      penaltyMinutes: row.penaltyMinutes,
      gameWinningGoals: row.gameWinningGoals,
      powerPlayGoals: row.powerPlayGoals,
      shortHandedGoals: row.shortHandedGoals,
      shootoutAttempts: row.shootoutAttempts,
      shootoutGoals: row.shootoutGoals,
      shootoutPct: row.shootoutPct,
    };
  });

  const byPoints = [...stats].sort(sortBy('points', 'goals', 'assists'));
  return {
    all: byPoints,
    points: byPoints.filter((row) => row.points > 0).slice(0, 10),
    goals: [...stats].filter((row) => row.goals > 0).sort(sortBy('goals', 'points')).slice(0, 10),
    assists: [...stats].filter((row) => row.assists > 0).sort(sortBy('assists', 'points')).slice(0, 10),
    shots: [...stats].filter((row) => row.shots > 0).sort(sortBy('shots', 'points')).slice(0, 10),
    penaltyMinutes: [...stats].filter((row) => row.penaltyMinutes > 0).sort(sortBy('penaltyMinutes', 'points')).slice(0, 10),
  };
}

function buildGoalieLeaderboards(db: AppDatabase, tournamentId: Id): PublicGoalieLeader[] {
  const games = db.games.filter((game) => game.tournamentId === tournamentId);
  const gameIds = new Set(games.map((game) => game.id));
  const stats = computeGoalieStats({
    games,
    events: db.scoreEvents.filter((event) => gameIds.has(event.gameId)),
    results: db.gameResults.filter((result) => gameIds.has(result.gameId)),
    rosterEntries: db.rosterEntries
      .filter((entry) => entry.tournamentId === tournamentId)
      .map((entry) => ({ teamId: entry.teamId, playerId: entry.playerId, position: entry.position })),
  });
  return stats.map((row) => {
    const player = db.players.find((item) => item.id === row.playerId);
    const profile = player ? db.profiles.find((item) => item.id === player.profileId) : undefined;
    const team = db.teams.find((item) => item.id === row.teamId);
    return {
      playerId: row.playerId,
      playerName: profile?.displayName ?? 'Unknown goalie',
      teamId: row.teamId,
      teamName: team?.name,
      gamesPlayed: row.gamesPlayed,
      wins: row.wins,
      losses: row.losses,
      saves: row.saves,
      goalsAgainst: row.goalsAgainst,
      shotsAgainst: row.shotsAgainst,
      savePct: row.savePct,
      gaa: row.gaa,
      shutouts: row.shutouts,
      shootoutShotsAgainst: row.shootoutShotsAgainst ?? 0,
      shootoutSaves: row.shootoutSaves ?? 0,
      shootoutGoalsAgainst: row.shootoutGoalsAgainst ?? 0,
      shootoutSavePct: row.shootoutSavePct ?? 0,
    };
  });
}

type NumericLeaderKey = 'points' | 'goals' | 'assists' | 'shots' | 'penaltyMinutes' | 'gameWinningGoals' | 'gamesPlayed';

function sortBy(
  primary: NumericLeaderKey,
  secondary: NumericLeaderKey,
  tertiary: NumericLeaderKey = 'gamesPlayed',
): (a: PublicPlayerLeader, b: PublicPlayerLeader) => number {
  return (a, b) =>
    b[primary] - a[primary] ||
    b[secondary] - a[secondary] ||
    b[tertiary] - a[tertiary] ||
    a.playerName.localeCompare(b.playerName);
}

function buildLeaders(db: AppDatabase, tournamentId: Id): PublicEventSnapshot['leaders'] {
  const games = db.games.filter((game) => game.tournamentId === tournamentId);
  const gameIds = new Set(games.map((game) => game.id));
  const stats = new Map<Id, { playerId: Id; goals: number; assists: number; points: number }>();
  for (const event of db.scoreEvents) {
    if (!gameIds.has(event.gameId) || !event.playerId) continue;
    const current = stats.get(event.playerId) ?? { playerId: event.playerId, goals: 0, assists: 0, points: 0 };
    if (event.type === 'goal' || event.type === 'penalty_shot_made') current.goals += 1;
    if (event.type === 'assist') current.assists += 1;
    current.points = current.goals + current.assists;
    stats.set(event.playerId, current);
  }
  return Array.from(stats.values())
    .sort((a, b) => b.points - a.points || b.goals - a.goals)
    .slice(0, 5)
    .map((row) => {
      const player = db.players.find((item) => item.id === row.playerId);
      const profile = player ? db.profiles.find((item) => item.id === player.profileId) : undefined;
      const roster = db.rosterEntries.find((item) => item.tournamentId === tournamentId && item.playerId === row.playerId);
      const team = roster ? db.teams.find((item) => item.id === roster.teamId) : undefined;
      return {
        ...row,
        playerName: profile?.displayName ?? 'Unknown player',
        teamName: team?.name,
      };
    });
}
